import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, ViewEncapsulation, afterRenderEffect, computed, effect, inject, input, linkedSignal, output, signal, untracked, viewChild } from '@angular/core';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { TooltipModule } from 'primeng/tooltip';

import { fitPageScale, fitWidthScale } from './doc.zoom.fit';
import { docZoomFrameBridgeStyles, docZoomFrameStyleId } from './doc.zoom.frame.theme';

type ZoomMode = 'fit-width' | 'fit-page' | 'manual';

/** Where a remount should start: the mode the user left, the manual scale, and the scale last painted. */
export interface DocZoomState {
    mode: ZoomMode;
    manualScale: number;
    scale: number;
}

/**
 * Self-contained document viewer with zoom/fit controls (Chrome-PDF style). The frame IS the scroll canvas: it
 * owns the overflow, scrollbar gutter, padding and background, projects an authored-width document, scales it to
 * fit, and pins a floating zoom pill (− / % / + / fit-toggle). The consumer just drops it into a sized flex/grid
 * cell and projects the document — no external scroll container or padding to wire up.
 */
@Component({
    selector: 'w-doc-zoom-frame',
    templateUrl: './doc.zoom.frame.component.html',
    styleUrls: ['./doc.zoom.frame.component.less'],
    standalone: true,
    imports: [TooltipModule],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        // The host is the scroll canvas: padding and background are bound from inputs so each consumer can tune the
        // gutter/backdrop without owning the scroll itself.
        '[style.padding.px]': '$padding()',
        '[style.padding-top.px]': '$resolvedPaddingY()',
        '[style.padding-right.px]': '$flushScrollbarRight() ? 0 : $resolvedPaddingX()',
        '[style.padding-bottom.px]': '$resolvedPaddingY()',
        '[style.padding-left.px]': '$resolvedPaddingX()',
        '[style.background]': '$background()',
        '[style.overflow-y]': '$overflowY()',
    },
})
export class DocZoomFrameComponent {

    private readonly document = inject(DOCUMENT);
    private readonly host = inject(ElementRef<HTMLElement>);
    private readonly destroyRef = inject(DestroyRef);

    // Authored width of the projected document (px); fit/scale math is relative to this.
    public readonly $docWidth = input(794, { alias: 'docWidth' });
    // When enabled, docWidth becomes a maximum: narrower canvases author the document at their available width.
    // This preserves nested overflow/sticky behavior while still capping and centring the document on wide screens.
    public readonly $fluidWidth = input(false, { alias: 'fluidWidth' });
    // Gives short projected documents the available canvas height without constraining longer content.
    public readonly $fillHeight = input(false, { alias: 'fillHeight' });
    public readonly $minZoom = input(0.25, { alias: 'minZoom' });
    public readonly $maxZoom = input(3, { alias: 'maxZoom' });
    public readonly $zoomStep = input(0.1, { alias: 'zoomStep' });
    // Breathing room (px, each side) baked into the fit scale on top of the canvas's own padding.
    public readonly $fitPadding = input(0, { alias: 'fitPadding' });
    // Initial fit mode ('fit-width' | 'fit-page'); the zoom pill takes over from the first interaction.
    public readonly $initialFit = input<'fit-width' | 'fit-page'>('fit-width', { alias: 'initialFit' });
    // Canvas gutter (px, all sides) and backdrop the frame paints around the document — the frame owns both now
    // that it is the scroll container. Defaults match the original consumer canvas (32px / secondary background).
    public readonly $padding = input(32, { alias: 'padding' });
    public readonly $paddingX = input<number | undefined>(undefined, { alias: 'paddingX' });
    public readonly $paddingY = input<number | undefined>(undefined, { alias: 'paddingY' });
    public readonly $flushScrollbarRight = input(false, { alias: 'flushScrollbarRight' });
    public readonly $background = input('var(--wb-color-background-secondary, #fafafa)', { alias: 'background' });
    // A previous mount's state. Paints at its scale until this canvas is measured, and keeps a manual zoom.
    public readonly $seed = input<DocZoomState | undefined>(undefined, { alias: 'seed' });
    // The host's own layout state. Changing it re-measures inside the same render pass: a ResizeObserver
    // reports after paint, so a rail column toggled by the host would otherwise paint one frame at the old fit.
    public readonly $layoutKey = input<unknown>(undefined, { alias: 'layoutKey' });
    public readonly stateChange = output<DocZoomState>();

    protected readonly $resolvedPaddingX = computed(() => this.$paddingX() ?? this.$padding());
    protected readonly $resolvedPaddingY = computed(() => this.$paddingY() ?? this.$padding());

    // Sticky fit: while in a fit mode, resize / content change re-fits; pressing −/+ pins a manual scale.
    // Seeded from the remembered state, else initialFit; resets if either input changes.
    protected readonly $zoomMode = linkedSignal<ZoomMode>(() => this.$seed()?.mode ?? this.$initialFit());
    private readonly $manualScale = linkedSignal<number>(() => this.$seed()?.manualScale ?? 1);
    private readonly $seedScale = computed<number>(() => this.$seed()?.scale ?? 0);
    private readonly $canvasSize = signal<{ width: number; height: number }>({ width: 0, height: 0 });
    private readonly $docNaturalHeight = signal(0);
    // Height of the canvas's horizontal scrollbar (0 when absent). Used to keep the floating dock pinned at a
    // constant gap from the canvas bottom edge when zooming past fit reveals/hides the h-scrollbar.
    private readonly $canvasScrollbarH = signal(0);

    private readonly $documentCanvasWidth = computed<number>(() => Math.max(0, this.$canvasSize().width - (this.$flushScrollbarRight() ? this.$resolvedPaddingX() : 0)));

    protected readonly $effectiveDocWidth = computed<number>(() => {
        if (!this.$fluidWidth()) return this.$docWidth();
        const canvasWidth = this.$documentCanvasWidth();
        return canvasWidth > 0 ? Math.min(this.$docWidth(), canvasWidth) : this.$docWidth();
    });

    // The scaler carries the document at its effective width; its offsetHeight is the doc's pre-transform height.
    private readonly $scalerEl = viewChild<ElementRef<HTMLElement>>('scaler');

    // Width is the frame's own measured box, i.e. what the host layout leaves beside any docked rail.
    private readonly $fitWidthScale = computed<number>(() => fitWidthScale(this.$documentCanvasWidth(), this.$effectiveDocWidth(), this.$fitPadding()));

    private readonly $fitPageScale = computed<number>(() => fitPageScale({ width: this.$documentCanvasWidth(), height: this.$canvasSize().height }, this.$effectiveDocWidth(), this.$docNaturalHeight(), this.$fitPadding()));

    private readonly $pageMeasured = computed<boolean>(() => this.$canvasSize().height > 0 && this.$docNaturalHeight() > 0);

    // Until this mount has measured, a seed stands in for the fit; equal numbers then leave the paint untouched.
    protected readonly $scale = computed<number>(() => {
        switch (this.$zoomMode()) {
            case 'fit-page': return this.$pageMeasured() || !this.$seedScale() ? this.$fitPageScale() : this.$seedScale();
            case 'manual': return this.$manualScale();
            default: return this.$fitWidthScale() || this.$seedScale();
        }
    });

    // Gate the doc's first paint until the ACTIVE fit mode's scale is final. The trap: fit-page falls back to
    // fit-width until the doc height is measured, and fit-width clamps to 1.0 on a wide canvas — so a naive
    // "scale > 0" gate reveals the doc at 100% for the frame between the canvas being measured and the doc height
    // arriving (two separate ResizeObserver cycles), then it snaps down. fit-page therefore also requires a
    // measured canvas height AND doc height. Manual zoom is user-driven, so it is always measured.
    protected readonly $measured = computed<boolean>(() => {
        if (this.$zoomMode() === 'manual') return true;
        if (this.$seedScale() > 0) return this.$scale() > 0;
        if (this.$zoomMode() === 'fit-page') return this.$canvasSize().height > 0 && this.$docNaturalHeight() > 0 && this.$scale() > 0;
        return this.$scale() > 0;
    });

    // The viewport reserves the SCALED box so the canvas scroll/centring match what's visible — a bare
    // transform: scale keeps the element's original layout box, which would mis-size the scroll area.
    protected readonly $scaledWidth = computed<number>(() => this.$effectiveDocWidth() * this.$scale());
    protected readonly $scaledHeight = computed<number>(() => (this.$docNaturalHeight() || this.$effectiveDocWidth()) * this.$scale());

    protected readonly $zoomPercent = computed<number>(() => Math.round(this.$scale() * 100));

    // Stage min-height = canvas CONTENT height (not CSS 100%, which resolves to the padding-box and overflows by
    // the canvas padding). Keeps the stage exactly tall enough to pin the dock at the bottom without adding scroll.
    protected readonly $canvasContentHeight = computed<number>(() => this.$canvasSize().height);
    protected readonly $documentMinHeight = computed<number | null>(() => this.$fillHeight() ? this.$canvasContentHeight() || null : null);
    protected readonly $overflowY = computed<'auto' | 'hidden'>(() => {
        const canvasHeight = this.$canvasContentHeight();
        return canvasHeight > 0 && this.$scaledHeight() > canvasHeight + 1 ? 'auto' : 'hidden';
    });

    // The fit toggle flips Width ⇄ Page; from manual it lands on Fit Width. Icon/tooltip show the NEXT action.
    protected readonly $nextFit = computed<'fit-width' | 'fit-page'>(() => this.$zoomMode() === 'fit-width' ? 'fit-page' : 'fit-width');

    // The frame's own padding now provides the gap from the canvas edge, so the base offset is 0 — the pill sits
    // at the padding-box bottom. Sticky `bottom` is measured from the scrollport, which shrinks by the h-scrollbar
    // height when it appears; subtracting it keeps the pill pinned to the same spot instead of jumping up when
    // zooming past fit reveals the h-scrollbar.
    protected readonly $dockBottom = computed<number>(() => -this.$canvasScrollbarH());

    public constructor() {

        // Bridge the pill/control colours to the v2 DesignSystem tokens (mounts a shared <style> once per id).
        DesignSystem.Mount(this.document, docZoomFrameStyleId, docZoomFrameBridgeStyles);

        const view = this.document.defaultView;
        let canvasRo: ResizeObserver | undefined;
        let docRo: ResizeObserver | undefined;

        effect(() => {
            if (!this.$measured()) return;
            const state: DocZoomState = { mode: this.$zoomMode(), manualScale: this.$manualScale(), scale: this.$scale() };
            untracked(() => this.stateChange.emit(state));
        });

        afterRenderEffect({ read: (onCleanup) => {
            this.$layoutKey();
            const scalerEl = this.$scalerEl()?.nativeElement;
            canvasRo?.disconnect();
            docRo?.disconnect();
            if (!view?.ResizeObserver) return;

            // The frame's own host element IS the scroll canvas: it owns overflow/gutter/padding (set in LESS +
            // host bindings), so we measure and observe it directly rather than hunting for a scroll ancestor.
            const canvas = this.host.nativeElement;

            const readCanvas = () => {
                const cs = view.getComputedStyle(canvas);
                const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
                const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
                const borderY = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
                this.$canvasSize.set({ width: Math.max(0, canvas.clientWidth - padX), height: Math.max(0, canvas.clientHeight - padY) });
                // offsetHeight − clientHeight − borders = the h-scrollbar height (0 when not shown).
                this.$canvasScrollbarH.set(Math.max(0, canvas.offsetHeight - canvas.clientHeight - borderY));
            };
            canvasRo = new view.ResizeObserver(readCanvas);
            canvasRo.observe(canvas);
            // Host padding is applied now. Measure before first paint instead of waiting for resize
            // delivery, which can leave a newly mounted document hidden for a frame (or longer).
            readCanvas();

            if (scalerEl) {
                // offsetHeight is the pre-transform layout height (transform doesn't change it), so it stays
                // stable across scale changes — no feedback loop.
                const readDoc = () => this.$docNaturalHeight.set(scalerEl.offsetHeight);
                docRo = new view.ResizeObserver(readDoc);
                docRo.observe(scalerEl);
                readDoc();
            }

            onCleanup(() => { canvasRo?.disconnect(); docRo?.disconnect(); });
        } });

        this.destroyRef.onDestroy(() => { canvasRo?.disconnect(); docRo?.disconnect(); });
    }

    protected zoomIn(): void {

        this.setManualScale(this.$scale() + this.$zoomStep());
    }

    protected zoomOut(): void {

        this.setManualScale(this.$scale() - this.$zoomStep());
    }

    // Clicking the % label resets to Actual Size (100%).
    protected resetZoom(): void {

        this.setManualScale(1);
    }

    protected toggleFit(): void {

        this.$zoomMode.set(this.$nextFit());
    }

    private setManualScale(scale: number): void {

        const clamped = Math.min(this.$maxZoom(), Math.max(this.$minZoom(), scale));
        this.$manualScale.set(Math.round(clamped * 100) / 100);
        this.$zoomMode.set('manual');
    }
}
